import { useCallback, useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import type { MasterLookupRecord, ProductAttributeDef } from "@/types";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const EMPTY = { label: "", attr_key: "", input_type: "TEXT" as ProductAttributeDef["input_type"], options: "", unit_label: "", required: false, sort_order: "" };

/** A readable key from a label: "Width (inch)" becomes "width_inch" */
const slug = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9]+/, "")
    .slice(0, 40);

interface Props {
  open: boolean;
  productTypes: MasterLookupRecord[];
  onClose: () => void;
  /** Called after any change so the product form can reload its fields */
  onChanged: () => void;
}

/** Add, change and remove the specification fields of each product type (GSM, width, count, finish...) */
export function SpecFieldsDialog({ open, productTypes, onClose, onChanged }: Props) {
  const { success, error } = useToast();
  const [type, setType] = useState("");
  const [defs, setDefs] = useState<ProductAttributeDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ProductAttributeDef | "new" | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !type && productTypes.length) setType(productTypes[0]!.code);
  }, [open, productTypes]);

  const load = useCallback(async () => {
    if (!type) return;
    setLoading(true);
    try {
      setDefs(unwrapArray<ProductAttributeDef>(await api.get(`/api/Product/AttributeDefs?product_type=${type}`)));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load the fields");
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    if (open) load();
    setEditing(null);
  }, [open, load]);

  const openNew = () => {
    setForm({ ...EMPTY, sort_order: String((defs.at(-1)?.sort_order || 0) + 1) });
    setEditing("new");
  };

  const openEdit = (d: ProductAttributeDef) => {
    setForm({ label: d.label, attr_key: d.attr_key, input_type: d.input_type, options: (d.options || []).join(", "), unit_label: d.unit_label || "", required: d.required, sort_order: String(d.sort_order) });
    setEditing(d);
  };

  const save = async () => {
    if (!form.label.trim()) return error("Validation Error", "Enter a label");
    const key = editing === "new" ? form.attr_key.trim() || slug(form.label) : form.attr_key;
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(key)) return error("Validation Error", "The key must start with a letter and use lowercase letters, digits and underscores");
    const options = form.options.split(",").map((o) => o.trim()).filter(Boolean);
    if (form.input_type === "SELECT" && options.length === 0) return error("Validation Error", "Add the choices, separated by commas");
    const payload = {
      product_type: type,
      attr_key: key,
      label: form.label.trim(),
      input_type: form.input_type,
      options: form.input_type === "SELECT" ? options : [],
      unit_label: form.unit_label.trim() || undefined,
      required: form.required,
      sort_order: form.sort_order === "" ? 0 : Math.round(Number(form.sort_order)),
    };
    setSaving(true);
    try {
      if (editing === "new") await api.post("/api/Product/AttributeDef/Insert", payload);
      else if (editing) await api.put(`/api/Product/AttributeDef/Update/${editing.id}`, payload);
      success("Field saved", "It shows on the product form straight away");
      setEditing(null);
      await load();
      onChanged();
    } catch (err: any) {
      error("Save Failed", err.message || "Could not save the field");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d: ProductAttributeDef) => {
    try {
      await api.delete(`/api/Product/AttributeDef/Delete/${d.id}`);
      success("Field removed", "Values already saved on products are kept");
      await load();
      onChanged();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not remove the field");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Specification fields</DialogTitle>
          <DialogDescription>The details asked for each kind of product: GSM and width for fabric, count and ply for yarn. They appear on the product form, quotes and WhatsApp cards.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex flex-wrap gap-1.5">
            {productTypes.map((t) => (
              <button
                key={t.code}
                type="button"
                onClick={() => setType(t.code)}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${type === t.code ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
              >
                {t.name}
              </button>
            ))}
          </div>

          {loading ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {defs.map((d) => (
                <li key={d.id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {d.label}
                      {d.unit_label ? <span className="text-muted-foreground font-normal"> ({d.unit_label})</span> : null}
                      {d.required ? <span className="text-red-500"> *</span> : null}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      <code className="font-mono">{d.attr_key}</code> · {d.input_type === "SELECT" ? `Choice: ${(d.options || []).join(", ")}` : d.input_type === "NUMBER" ? "Number" : "Text"}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button type="button" variant="ghost" size="icon-sm" onClick={() => openEdit(d)} title="Edit">
                      <Pencil className="size-4 text-muted-foreground" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon-sm" onClick={() => remove(d)} title="Remove">
                      <Trash2 className="size-4 text-red-500" />
                    </Button>
                  </div>
                </li>
              ))}
              {defs.length === 0 && <li className="px-3 py-4 text-sm text-muted-foreground">No fields for this type yet.</li>}
            </ul>
          )}

          {editing ? (
            <div className="rounded-lg bg-muted/40 p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Label *</Label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, attr_key: editing === "new" ? slug(e.target.value) : form.attr_key })} placeholder="e.g. Thread count" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Key</Label>
                  <Input value={form.attr_key} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, attr_key: e.target.value })} className="font-mono" />
                  {editing !== "new" && <p className="text-[11px] text-muted-foreground">The key stays fixed because products store their values under it.</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Kind of value</Label>
                  <select value={form.input_type} onChange={(e) => setForm({ ...form, input_type: e.target.value as any })} className={field}>
                    <option value="TEXT">Text</option>
                    <option value="NUMBER">Number</option>
                    <option value="SELECT">Choice from a list</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Unit shown</Label>
                  <Input value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} placeholder="GSM, inch, %..." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Order</Label>
                  <Input type="number" step="1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
                </div>
              </div>
              {form.input_type === "SELECT" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Choices, separated by commas *</Label>
                  <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="Woven, Knitted, Non-woven" />
                </div>
              )}
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                Required when saving a product of this type
              </label>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditing(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button type="button" size="sm" className="h-8 text-xs" onClick={save} disabled={saving}>
                  {saving ? <Loader2 className="size-3.5 animate-spin mr-1.5" /> : null}
                  Save field
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={openNew} disabled={!type}>
              <Plus className="size-3.5" /> Add a field
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
